/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { ShoppingCartItemPaymentType } from 'types/enums/ShoppingCartItemPaymentType'

interface ReservationCheckoutPlannerModalItemProps {
    plan: {
        article_group_id: number
        payment_type: ShoppingCartItemPaymentType
        is_active: boolean
    }
    getArticleGroupName: (id: number) => string
    handlePaymentTypeChange: (
        id: number,
        type: ShoppingCartItemPaymentType
    ) => void
    handleActiveToggle: (id: number) => void
}

const ReservationCheckoutPlannerModalItem: React.FC<
    ReservationCheckoutPlannerModalItemProps
> = ({
    plan,
    getArticleGroupName,
    handlePaymentTypeChange,
    handleActiveToggle,
}) => {
    const { t } = useTranslation()

    return (
        <div className="ReservationCheckoutPlannerModalItem flex items-center justify-between p-3.5 UiCard transition-all hover:border-gray-300 dark:hover:border-gray-600">
            <div className="flex-1 text-[15px] font-medium text-gray-700 dark:text-gray-300 pr-4">
                {`Artikelgruppe ${plan.article_group_id} | ${getArticleGroupName(plan.article_group_id)}`}
            </div>

            <div className="flex items-center gap-8">
                <div className="flex border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden bg-white dark:bg-gray-800">
                    <button
                        type="button"
                        onClick={() =>
                            handlePaymentTypeChange(
                                plan.article_group_id,
                                ShoppingCartItemPaymentType.Checkout
                            )
                        }
                        className={clsx(
                            'px-7 py-2.5 text-[14px] font-medium transition-all',
                            plan.payment_type ===
                                ShoppingCartItemPaymentType.Checkout
                                ? 'bg-gray-200 text-[#4666ff] dark:bg-gray-700 dark:text-blue-400'
                                : 'text-gray-800 dark:text-gray-200'
                        )}
                    >
                        {t('checkout')}
                    </button>
                    <div className="w-[1px] bg-gray-300 dark:bg-gray-600" />
                    <button
                        type="button"
                        onClick={() =>
                            handlePaymentTypeChange(
                                plan.article_group_id,
                                ShoppingCartItemPaymentType.Invoice
                            )
                        }
                        className={clsx(
                            'px-7 py-2.5 text-[14px] font-medium transition-all',
                            plan.payment_type ===
                                ShoppingCartItemPaymentType.Invoice
                                ? 'bg-gray-200 text-[#4666ff] dark:bg-gray-700 dark:text-blue-400'
                                : 'text-gray-800 dark:text-gray-200'
                        )}
                    >
                        {t('invoice')}
                    </button>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        checked={plan.is_active}
                        onChange={() =>
                            handleActiveToggle(plan.article_group_id)
                        }
                        className="sr-only peer"
                    />
                    <div
                        className={clsx(
                            'w-[52px] h-[28px] bg-gray-300 dark:bg-gray-700 peer-focus:outline-none rounded-full transition-all duration-300',
                            'after:content-[""] after:absolute after:top-[3px] after:left-[4px] after:bg-white after:rounded-full after:h-[22px] after:w-[22px] after:transition-all',
                            'peer-checked:bg-[#4666ff] peer-checked:after:translate-x-[22px]'
                        )}
                    ></div>
                </label>
            </div>
        </div>
    )
}

export default ReservationCheckoutPlannerModalItem
